import {
  FlowUpdateProduct,
  ResponseGetMerchflow_merchflowId_FlowUpdateProducts,
} from "@CommonApi/merchflow/_merchflowId_/flow-update/products";
import { ProductUpdateAdd, ProductUpdateRemove, ProductUpdates, ProductUpdateSwap } from "./types";

/**
 * The interfaces here are in snake_case, because these are the
 * column names in the product updates spreadsheet file.
 */
export interface SwapProductRaw {
  add_product_code?: string;
  pnl_decision?: string;
  remove_product_code?: string;
  swap_facings_mode?: unknown;
  stores_codes?: string;
}

export interface AddProductRaw {
  add_product_code?: string;
  place_to?: string;
  adjacent_product_code?: string;
  facings?: string;
  reducible_product_code?: string | undefined;
  fit_strategy?: string;
  pnl_decision?: string;
  stores_codes?: string;
}

export interface RemoveProductRaw {
  remove_product_code?: string;
  pnl_decision?: string;
  stores_codes?: string;
}

export type UploadErrorOperator = "Swap" | "Add" | "Remove";

export interface UploadError {
  operator: UploadErrorOperator;
  rowNumber: number;
  message: string;
}

export const productsUpdatesValidator = (
  products: ResponseGetMerchflow_merchflowId_FlowUpdateProducts,
  swapProductsRaw: SwapProductRaw[],
  addProductsRaw: AddProductRaw[],
  removeProductsRaw: RemoveProductRaw[],
  availableStoreCodes?: Set<string>,
) => {
  const productsUpdates: ProductUpdates = [];
  const errors: UploadError[] = [];

  for (let i = 0; i < swapProductsRaw.length; i++) {
    const swapProductRaw = swapProductsRaw[i];
    const rowNumber = i + 2;

    if (
      !swapProductRaw.remove_product_code ||
      !swapProductRaw.add_product_code ||
      !swapProductRaw.pnl_decision
    ) {
      const missingFields: string[] = [];
      if (!swapProductRaw.remove_product_code) missingFields.push('"remove_product_code"');
      if (!swapProductRaw.add_product_code) missingFields.push('"add_product_code"');
      if (!swapProductRaw.pnl_decision) missingFields.push('"pnl_decision"');
      errors.push({
        operator: "Swap",
        rowNumber,
        message: `Missing required field(s): ${missingFields.join(", ")}`,
      });
      continue;
    }

    if (!isValidPnlDecision(swapProductRaw.pnl_decision)) {
      errors.push({
        operator: "Swap",
        rowNumber,
        message: `Invalid pnl_decision "${swapProductRaw.pnl_decision}". Allowed values: DEFAULT, MOVEMENT, SALES, PROFIT.`,
      });
      continue;
    }

    const removeProduct = products.removableProducts.find(
      (product) => product.productCode === String(swapProductRaw.remove_product_code),
    );
    if (!removeProduct) {
      errors.push({
        operator: "Swap",
        rowNumber,
        message: `Product code "${swapProductRaw.remove_product_code ?? ""}" is not in the removable product list`,
      });
      continue;
    }

    const addProductCodes = String(swapProductRaw.add_product_code)
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c !== "");

    if (addProductCodes.length === 0) {
      errors.push({
        operator: "Swap",
        rowNumber,
        message: '"add_product_code" must contain at least one product code',
      });
      continue;
    }

    const addProducts: typeof products.addableProducts = [];
    let hasInvalidAddCode = false;
    for (const code of addProductCodes) {
      const addProduct = products.addableProducts.find((product) => product.productCode === code);
      if (!addProduct) {
        errors.push({
          operator: "Swap",
          rowNumber,
          message: `Product code "${code}" is not in the addable product list`,
        });
        hasInvalidAddCode = true;
        break;
      }
      addProducts.push(addProduct);
    }
    if (hasInvalidAddCode) continue;

    let storeCodes = parseStoreCodes(swapProductRaw.stores_codes);

    if (availableStoreCodes && storeCodes.length > 0) {
      const validStoreCodes = storeCodes.filter((storeCode) => availableStoreCodes.has(storeCode));
      const invalidStoreCodes = storeCodes.filter(
        (storeCode) => !availableStoreCodes.has(storeCode),
      );

      if (validStoreCodes.length === 0) {
        errors.push({
          operator: "Swap",
          rowNumber,
          message: `All store codes are invalid [${invalidStoreCodes.join(", ")}]; no changes were applied.`,
        });
        continue;
      }

      if (invalidStoreCodes.length > 0) {
        errors.push({
          operator: "Swap",
          rowNumber,
          message: `Ignored invalid store codes [${invalidStoreCodes.join(
            ", ",
          )}]; applied to stores [${validStoreCodes.join(", ")}].`,
        });
      }

      storeCodes = validStoreCodes;
    }

    const rawSwapFacingsMode =
      swapProductRaw.swap_facings_mode == null
        ? undefined
        : String(swapProductRaw.swap_facings_mode).trim().toUpperCase();
    const swapFacingsMode =
      rawSwapFacingsMode === "MATCH" || rawSwapFacingsMode === "DOS_MOS_BALANCE"
        ? rawSwapFacingsMode
        : rawSwapFacingsMode !== undefined && rawSwapFacingsMode !== ""
          ? (() => {
              errors.push({
                operator: "Swap",
                rowNumber,
                message: `Invalid swap_facings_mode "${String(swapProductRaw.swap_facings_mode)}". Allowed values: MATCH, DOS_MOS_BALANCE.`,
              });
              return undefined;
            })()
          : "DOS_MOS_BALANCE";

    if (swapFacingsMode === undefined) continue;

    productsUpdates.push({
      type: "swap",
      remove: removeProduct,
      add: addProducts,
      pnlDecision: swapProductRaw.pnl_decision,
      storeCodes,
      // canSqueezeSwapTo is not configurable via XLSX upload — it is a UI-only toggle
      // that users set per swap operation in the product review step.
      canSqueezeSwapTo: false,
      swapFacingsMode,
    } satisfies ProductUpdateSwap);
  }

  for (let i = 0; i < addProductsRaw.length; i++) {
    const addProductRaw = addProductsRaw[i];
    const rowNumber = i + 2;

    if (
      !addProductRaw.add_product_code ||
      !addProductRaw.fit_strategy ||
      !addProductRaw.pnl_decision
    ) {
      const missingFields: string[] = [];
      if (!addProductRaw.add_product_code) missingFields.push('"add_product_code"');
      if (!addProductRaw.fit_strategy) missingFields.push('"fit_strategy"');
      if (!addProductRaw.pnl_decision) missingFields.push('"pnl_decision"');
      errors.push({
        operator: "Add",
        rowNumber,
        message: `Missing required field(s): ${missingFields.join(", ")}`,
      });
      continue;
    }

    if (
      addProductRaw.fit_strategy !== "KEEP_ALL_FACINGS" &&
      addProductRaw.fit_strategy !== "CAN_REDUCE_FACINGS" &&
      addProductRaw.fit_strategy !== "CAN_DELETE_RANGED_PRODUCTS" &&
      addProductRaw.fit_strategy !== "CAN_DELETE_RANGED_PRODUCTS_INCLUDING_CORE_RANGE"
    ) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `Invalid fit_strategy "${addProductRaw.fit_strategy}". Allowed values: KEEP_ALL_FACINGS, CAN_REDUCE_FACINGS, CAN_DELETE_RANGED_PRODUCTS, CAN_DELETE_RANGED_PRODUCTS_INCLUDING_CORE_RANGE.`,
      });
      continue;
    }

    if (
      addProductRaw.facings !== "AUTO" &&
      addProductRaw.facings !== undefined &&
      (!/^\d+$/.test(addProductRaw.facings) || parseInt(addProductRaw.facings) <= 0)
    ) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `Facings "${addProductRaw.facings}" must be "AUTO" or a positive integer.`,
      });
      continue;
    }

    if (!isValidPnlDecision(addProductRaw.pnl_decision)) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `Invalid pnl_decision "${addProductRaw.pnl_decision}". Allowed values: DEFAULT, MOVEMENT, SALES, PROFIT.`,
      });
      continue;
    }

    const addProduct = products.addableProducts.find(
      (product) => product.productCode === String(addProductRaw.add_product_code),
    );
    if (!addProduct) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `Product code "${addProductRaw.add_product_code ?? ""}" is not in the addable product list.`,
      });
      continue;
    }

    if (
      addProductRaw.place_to &&
      addProductRaw.place_to !== "LEFT" &&
      addProductRaw.place_to !== "RIGHT"
    ) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `place_to "${addProductRaw.place_to}" must be "LEFT" or "RIGHT".`,
      });
      continue;
    }

    const adjacentProductPlaceTo =
      (addProductRaw.place_to === "LEFT" && "LEFT") ||
      (addProductRaw.place_to === "RIGHT" && "RIGHT") ||
      null;

    if (addProductRaw.adjacent_product_code && !adjacentProductPlaceTo) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `"adjacent_product_code" is set to "${addProductRaw.adjacent_product_code}", but "place_to" is missing or invalid.`,
      });
      continue;
    }

    if (!addProductRaw.adjacent_product_code && adjacentProductPlaceTo) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `"place_to" is set to "${addProductRaw.place_to}", but "adjacent_product_code" is missing.`,
      });
      continue;
    }

    let adjacentProduct: FlowUpdateProduct | null = null;
    if (adjacentProductPlaceTo) {
      adjacentProduct =
        products.removableProducts.find(
          (product) => product.productCode === String(addProductRaw.adjacent_product_code),
        ) ?? null;
      if (!adjacentProduct) {
        errors.push({
          operator: "Add",
          rowNumber,
          message: `adjacent_product_code "${addProductRaw.adjacent_product_code ?? ""}" is not in the removable products list.`,
        });
        continue;
      }
    }

    const reducibleProduct = addProductRaw.reducible_product_code
      ? products.removableProducts.find(
          (product) => product.productCode === String(addProductRaw.reducible_product_code),
        )
      : undefined;
    if (addProductRaw.reducible_product_code && !reducibleProduct) {
      errors.push({
        operator: "Add",
        rowNumber,
        message: `reducible_product_code "${addProductRaw.reducible_product_code ?? ""}" is not in the removable products list.`,
      });
      continue;
    }

    let storeCodes = parseStoreCodes(addProductRaw.stores_codes);

    if (availableStoreCodes && storeCodes.length > 0) {
      const validStoreCodes = storeCodes.filter((storeCode) => availableStoreCodes.has(storeCode));
      const invalidStoreCodes = storeCodes.filter(
        (storeCode) => !availableStoreCodes.has(storeCode),
      );

      if (validStoreCodes.length === 0) {
        errors.push({
          operator: "Add",
          rowNumber,
          message: `All store codes are invalid [${invalidStoreCodes.join(
            ", ",
          )}]; no changes were applied.`,
        });
        continue;
      }

      if (invalidStoreCodes.length > 0) {
        errors.push({
          operator: "Add",
          rowNumber,
          message: `Ignored invalid store codes [${invalidStoreCodes.join(
            ", ",
          )}]; applied to stores [${validStoreCodes.join(", ")}].`,
        });
      }

      storeCodes = validStoreCodes;
    }

    productsUpdates.push({
      type: "add",
      productToAdd: addProduct,
      adjacentPosition: adjacentProductPlaceTo,
      adjacentProduct,
      facings: addProductRaw.facings || "AUTO",
      reducibleProduct: reducibleProduct ?? null,
      fitStrategy: addProductRaw.fit_strategy,
      pnlDecision: addProductRaw.pnl_decision,
      storeCodes,
      canBeSqueezed: false,
    } satisfies ProductUpdateAdd);
  }

  for (let i = 0; i < removeProductsRaw.length; i++) {
    const removeProductRaw = removeProductsRaw[i];
    const rowNumber = i + 2;

    if (!removeProductRaw.remove_product_code || !removeProductRaw.pnl_decision) {
      const missingFields: string[] = [];
      if (!removeProductRaw.remove_product_code) missingFields.push('"remove_product_code"');
      if (!removeProductRaw.pnl_decision) missingFields.push('"pnl_decision"');
      errors.push({
        operator: "Remove",
        rowNumber,
        message: `Missing required field(s): ${missingFields.join(", ")}`,
      });
      continue;
    }

    if (!isValidPnlDecision(removeProductRaw.pnl_decision)) {
      errors.push({
        operator: "Remove",
        rowNumber,
        message: `Invalid pnl_decision "${removeProductRaw.pnl_decision}". Allowed values: DEFAULT, MOVEMENT, SALES, PROFIT.`,
      });
      continue;
    }

    const removeProduct = products.removableProducts.find(
      (product) => product.productCode === String(removeProductRaw.remove_product_code),
    );
    if (!removeProduct) {
      errors.push({
        operator: "Remove",
        rowNumber,
        message: `Product code "${removeProductRaw.remove_product_code ?? ""}" is not in the removable product list.`,
      });
      continue;
    }

    let storeCodes = parseStoreCodes(removeProductRaw.stores_codes);

    if (availableStoreCodes && storeCodes.length > 0) {
      const validStoreCodes = storeCodes.filter((storeCode) => availableStoreCodes.has(storeCode));
      const invalidStoreCodes = storeCodes.filter(
        (storeCode) => !availableStoreCodes.has(storeCode),
      );

      if (validStoreCodes.length === 0) {
        errors.push({
          operator: "Remove",
          rowNumber,
          message: `All store codes are invalid [${invalidStoreCodes.join(
            ", ",
          )}]; no changes were applied.`,
        });
        continue;
      }

      if (invalidStoreCodes.length > 0) {
        errors.push({
          operator: "Remove",
          rowNumber,
          message: `Ignored invalid store codes [${invalidStoreCodes.join(
            ", ",
          )}]; applied to stores [${validStoreCodes.join(", ")}].`,
        });
      }

      storeCodes = validStoreCodes;
    }

    productsUpdates.push({
      type: "remove",
      product: removeProduct,
      pnlDecision: removeProductRaw.pnl_decision,
      storeCodes,
    } satisfies ProductUpdateRemove);
  }

  return { productsUpdates, errors };
};

const parseStoreCodes = (storeCodes?: string) => {
  return storeCodes
    ? String(storeCodes || "")
        .split(",")
        .map((storeCode) => storeCode.trim())
        .filter((storeCode) => storeCode !== "")
    : [];
};

const isValidPnlDecision = (pnlDecision: string) => {
  return (
    pnlDecision === "DEFAULT" ||
    pnlDecision === "MOVEMENT" ||
    pnlDecision === "SALES" ||
    pnlDecision === "PROFIT"
  );
};
